import { useState } from "react";
import {
    MoreVertical, BookOpen, Shield, Flag, AlertTriangle, Mail, Feather,
    ChevronRight, ChevronUp, Send, Loader2, AtSign, Globe, Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitAIReport } from "@/lib/api";
import PrivacySummary from "@/components/PrivacySummary";

const CONTACT_EMAIL = "HELPDESK@htokoki.com";
const CONTACT_WEBSITE_URL = "https://htokoki.com";
const CONTACT_WEBSITE_LABEL = "H. Tokoki";
const CONTACT_INSTAGRAM_URL = "https://instagram.com/hirotokoki";
const CONTACT_INSTAGRAM_HANDLE = "@hirotokoki";

const REPORT_CATEGORIES = [
    "Harmful or unsafe content",
    "Copyright concern",
    "Hallucination / factual error",
    "Bias or offensive output",
    "Broken formatting",
    "Other",
];

export default function HelpMenu({ triggerClassName = "", align = "end", variant = "dark" }) {
    const [openId, setOpenId] = useState(null);
    const [contactExpanded, setContactExpanded] = useState(false);
    const close = () => setOpenId(null);
    const baseTrigger =
        variant === "light"
            ? "bg-transparent hover:bg-accent/60 text-foreground/70 hover:text-foreground border border-border/60"
            : "bg-white/10 hover:bg-white/20 text-white";
    return (
        <>
            <DropdownMenu onOpenChange={(o) => { if (!o) setContactExpanded(false); }}>
                <DropdownMenuTrigger asChild>
                    <button
                        data-testid="help-menu-trigger"
                        aria-label="Open help menu"
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${baseTrigger} ${triggerClassName}`}
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={align} className="w-72 p-1.5" data-testid="help-menu-content">
                    <MenuRow testid="menu-item-how-to" icon={BookOpen} label="How To — Create a Story" onClick={() => setOpenId("how-to")} />
                    <MenuRow testid="menu-item-privacy" icon={Shield} label="Privacy Policy" onClick={() => setOpenId("privacy")} />
                    <MenuRow testid="menu-item-report" icon={Flag} label="Report AI content" tone="danger" onClick={() => setOpenId("report")} />
                    <MenuRow testid="menu-item-safety" icon={AlertTriangle} label="Content & Safety" onClick={() => setOpenId("safety")} />
                    <ContactRow expanded={contactExpanded} onToggle={() => setContactExpanded(v => !v)} />
                    <DropdownMenuSeparator className="my-1" />
                    <div className="flex items-center gap-2 px-2 py-1.5 text-xs italic text-muted-foreground">
                        <Feather className="w-3.5 h-3.5" />
                        <span>Brought to you by H.Tokoki</span>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>

            <HowToDialog open={openId === "how-to"} onClose={close} />
            <PrivacyDialog open={openId === "privacy"} onClose={close} />
            <ReportDialog open={openId === "report"} onClose={close} />
            <SafetyDialog open={openId === "safety"} onClose={close} />
        </>
    );
}

function MenuRow({ testid, icon: Icon, label, tone, onClick }) {
    const isDanger = tone === "danger";
    return (
        <DropdownMenuItem
            data-testid={testid}
            onSelect={(e) => { e.preventDefault(); onClick?.(); }}
            className={`flex items-center gap-3 px-2 py-2 cursor-pointer rounded-md ${isDanger ? "text-destructive focus:text-destructive" : ""}`}
        >
            <Icon className={`w-4 h-4 ${isDanger ? "text-destructive" : "text-primary"}`} />
            <span className="flex-1 text-sm">{label}</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </DropdownMenuItem>
    );
}

function ContactRow({ expanded, onToggle }) {
    return (
        <>
            <DropdownMenuItem
                data-testid="menu-item-contact"
                onSelect={(e) => { e.preventDefault(); onToggle(); }}
                className="flex items-center gap-3 px-2 py-2 cursor-pointer rounded-md"
            >
                <Mail className="w-4 h-4 text-primary" />
                <span className="flex-1 text-sm">Contact Us</span>
                {expanded
                    ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
            </DropdownMenuItem>
            {expanded && (
                <div data-testid="contact-submenu" className="pl-3 pr-1 pb-1 flex flex-col">
                    <ContactSubRow
                        testid="contact-sub-email"
                        icon={AtSign}
                        label={CONTACT_EMAIL}
                        href={`mailto:${CONTACT_EMAIL}`}
                    />
                    <ContactSubRow
                        testid="contact-sub-website"
                        icon={Globe}
                        label={CONTACT_WEBSITE_LABEL}
                        href={CONTACT_WEBSITE_URL}
                        external
                    />
                    <ContactSubRow
                        testid="contact-sub-instagram"
                        icon={Instagram}
                        label={CONTACT_INSTAGRAM_HANDLE}
                        href={CONTACT_INSTAGRAM_URL}
                        external
                    />
                </div>
            )}
        </>
    );
}

function ContactSubRow({ testid, icon: Icon, label, href, external }) {
    return (
        <a
            data-testid={testid}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="flex items-center gap-3 px-2 py-2 rounded-md text-sm hover:bg-accent/60 focus:bg-accent focus:outline-none"
        >
            <Icon className="w-4 h-4 text-primary" />
            <span className="flex-1 truncate">{label}</span>
        </a>
    );
}

// ---------------- Content dialogs ------------------

function HowToDialog({ open, onClose }) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="how-to-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-primary" /> How to create a story
                    </DialogTitle>
                    <DialogDescription>A short tour of your Day Dreamer desk.</DialogDescription>
                </DialogHeader>
                <ol className="space-y-4 text-sm leading-relaxed mt-2">
                    <Step n={1} title="Begin a new manuscript">
                        Tap <em>New Novel</em>, name your book, and add a genre, POV, tone, and one-line synopsis. You can edit any of these later.
                    </Step>
                    <Step n={2} title="Draft chapters">
                        Open a novel and add chapters from the left sidebar. The centre panel is a distraction-free editor. Set a per-chapter word goal so progress feels earned.
                    </Step>
                    <Step n={3} title="Write with AI as your co-pilot">
                        Switch to the <em>Write</em> or <em>Edit</em> panel on the right. Describe a scene to draft, or select passages to rewrite. New AI text lands as tracked changes so you can accept or reject.
                    </Step>
                    <Step n={4} title="Proofread with one-click fixes">
                        Open the <em>Proof</em> ribbon on the right edge. Run <em>Selection</em> or <em>Whole chapter</em>; each suggestion has Apply, Ignore, or Restore. Inline highlights remain even after ignoring.
                    </Step>
                    <Step n={5} title="Build your world">
                        Add characters in the <em>Cast</em> panel (with colour coding) and story beats in the <em>Plot</em> panel — both ribbon-pinned to the right so they follow you as you write.
                    </Step>
                    <Step n={6} title="Cover, share, export">
                        Upload a cover image, enable a beta-reader share link for private feedback, or export the finished book as DOCX, EPUB, or PDF from the top bar.
                    </Step>
                </ol>
            </DialogContent>
        </Dialog>
    );
}
function Step({ n, title, children }) {
    return (
        <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center tabular-nums">{n}</span>
            <div className="flex-1">
                <div className="font-medium text-foreground">{title}</div>
                <p className="text-muted-foreground mt-0.5">{children}</p>
            </div>
        </li>
    );
}

function PrivacyDialog({ open, onClose }) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="privacy-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                        <Shield className="w-5 h-5 text-primary" /> Privacy Policy
                    </DialogTitle>
                    <DialogDescription>A summary of how Day Dreamer handles your data.</DialogDescription>
                </DialogHeader>
                <div className="mt-2"><PrivacySummary onNavigate={onClose} /></div>
            </DialogContent>
        </Dialog>
    );
}
function Section({ title, children }) {
    return (
        <div>
            <div className="text-foreground font-medium text-sm mb-1">{title}</div>
            <p>{children}</p>
        </div>
    );
}

function ReportDialog({ open, onClose }) {
    const [category, setCategory] = useState(REPORT_CATEGORIES[0]);
    const [description, setDescription] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!description.trim()) { toast.error("Add a short description"); return; }
        setBusy(true);
        try {
            await submitAIReport({ category, description, excerpt });
            toast.success("Report submitted — thank you");
            setDescription(""); setExcerpt(""); setCategory(REPORT_CATEGORIES[0]);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to submit report");
        }
        setBusy(false);
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="report-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                        <Flag className="w-5 h-5 text-destructive" /> Report AI content
                    </DialogTitle>
                    <DialogDescription>Flag AI output that felt unsafe, wrong, or off. Your report reaches H.Tokoki directly.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-1">
                    <div>
                        <Label className="ui-label">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="report-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {REPORT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="ui-label">What happened?</Label>
                        <Textarea data-testid="report-description" rows={4} value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what the AI produced and why it’s a problem…" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="ui-label">Paste the offending excerpt (optional)</Label>
                        <Textarea data-testid="report-excerpt" rows={3} value={excerpt}
                            onChange={(e) => setExcerpt(e.target.value)}
                            placeholder="Copy the exact AI output here so we can review the source." className="mt-1.5" />
                    </div>
                    <DialogFooter>
                        <Button type="submit" data-testid="report-submit" disabled={busy} className="rounded-full">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Send report
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function SafetyDialog({ open, onClose }) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="safety-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-primary" /> Content & Safety
                    </DialogTitle>
                    <DialogDescription>How we keep Day Dreamer a safe place to write.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm leading-relaxed text-muted-foreground mt-2">
                    <Section title="What isn’t allowed">
                        Sexual content involving minors, real-world instructions for serious harm, content that targets a real identifiable person with defamation, and content that violates applicable law. Reports are reviewed and offending accounts are disabled.
                    </Section>
                    <Section title="Fiction is welcome, harm is not">
                        Difficult themes are part of literature. We support darkness, complexity, and moral ambiguity in your storytelling — the boundary is real-world harm, not fictional exploration.
                    </Section>
                    <Section title="AI limits">
                        The AI models used by Day Dreamer will refuse some prompts. When that happens, the assistant will tell you why and suggest a safer angle for the same scene.
                    </Section>
                    <Section title="Reporting">
                        Anything the AI produces that feels wrong can be flagged via <em>Report AI content</em>. Every report goes directly to H.Tokoki.
                    </Section>
                </div>
            </DialogContent>
        </Dialog>
    );
}


